import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api";
import "./../styles/Campaigns.css";
import NewCampaignModal from "../components/NewCampaignModal.jsx";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";
import CampaignTutorial from "../components/CampaignTutorial.jsx";

const GMAIL_LOGO = "https://i.imgur.com/Z7XGfxk.png";
const OUTLOOK_LOGO = "https://i.imgur.com/T2FXrTH.png";

function useDebouncedValue(value, delay = 300) {
  const [val, setVal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setVal(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return val;
}
const n = (v) => Number(v || 0);

async function apiListCampaigns() {
  const j = await apiFetch("/api/email/campaigns", { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiCreateCampaign(payload) {
  const j = await apiFetch("/api/email/campaigns", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return j?.id;
}
async function apiCloneCampaign(id) {
  const j = await apiFetch(`/api/email/campaigns/${id}/clone`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({}),
  });
  return j?.id;
}

async function apiListTemplates() {
  const j = await apiFetch("/api/email/templates", { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiCreateTemplate({ name, subject, html, text = null }) {
  const j = await apiFetch("/api/email/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
  return j?.id;
}

async function apiListMailboxes() {
  const j = await apiFetch("/api/mailboxes", { credentials: "include" });
  return j || { items: [] };
}
async function apiStartGoogle() {
  const j = await apiFetch("/api/mailboxes/google/start", {
    method: "POST",
    credentials: "include",
  });
  return j;
}
async function apiStartOutlook() {
  const j = await apiFetch("/api/mailboxes/outlook/start", {
    method: "POST",
    credentials: "include",
  });
  return j;
}
async function apiSkipMailbox() {
  try {
    await apiFetch("/api/mailboxes/skip", { method: "POST", credentials: "include" });
  } catch {}
}

const DEFAULT_TEMPLATE_NAME = "Default Outreach";
const DEFAULT_TEMPLATE_SUBJECT = "Feature request for {{playlist_name}}";
const DEFAULT_TEMPLATE_HTML = `<p>Hi {{curator_name}},</p><p>I came across your playlist <a href="{{playlist_url}}">{{playlist_name}}</a> and thought this release could be a great fit.</p><p>Best,<br>{{sender_name}}</p>`;
const DEFAULT_TEMPLATE_TEXT = `Hi {{curator_name}}, I came across your playlist {{playlist_name}} ({{playlist_url}}) and thought this release could be a great fit. Best, {{sender_name}}`;

async function ensureDefaultTemplate({ setTplLoading, setTplErr, setTemplates, setDefaultTplId }) {
  setTplLoading(true);
  setTplErr("");
  try {
    let items = await apiListTemplates();
    let def = items.find((t) => (t.name || "").toLowerCase() === DEFAULT_TEMPLATE_NAME.toLowerCase());
    if (!def) {
      const id = await apiCreateTemplate({
        name: DEFAULT_TEMPLATE_NAME,
        subject: DEFAULT_TEMPLATE_SUBJECT,
        html: DEFAULT_TEMPLATE_HTML,
        text: DEFAULT_TEMPLATE_TEXT,
      });
      def = { id, name: DEFAULT_TEMPLATE_NAME, subject: DEFAULT_TEMPLATE_SUBJECT, html: DEFAULT_TEMPLATE_HTML, text: DEFAULT_TEMPLATE_TEXT };
      items = [def, ...items];
    } else {
      items = [def, ...items.filter((x) => x.id !== def.id)];
    }
    setTemplates(items);
    setDefaultTplId(def.id);
  } catch (e) {
    setTplErr(e?.message || "Failed to load templates");
  } finally {
    setTplLoading(false);
  }
}

const SKIP_KEY = "mb_skip_v1";

export default function Campaign() {
  const seo = seoPresets["/campaign"];
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);

  const [notice, setNotice] = useState("");
  const [openNew, setOpenNew] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplErr, setTplErr] = useState("");
  const [defaultTplId, setDefaultTplId] = useState(null);

  const [showConnect, setShowConnect] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [agree, setAgree] = useState(false);

  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const [showCampaignTutorial, setShowCampaignTutorial] = useState(false);

  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpenMenuId(null);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpenMenuId(null);
    }
    function onScrollOrResize() {
      if (openMenuId !== null) setOpenMenuId(null);
    }
    if (openMenuId !== null) {
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onEsc);
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      return () => {
        document.removeEventListener("mousedown", onDocClick);
        document.removeEventListener("keydown", onEsc);
        window.removeEventListener("scroll", onScrollOrResize, true);
        window.removeEventListener("resize", onScrollOrResize);
      };
    }
  }, [openMenuId]);

  useEffect(() => {
    const done = localStorage.getItem("campaignTutorialCompleted") === "true";
    if (!done) {
      setShowCampaignTutorial(true);
    }
  }, []);

  async function refreshCampaigns() {
    setLoading(true);
    try {
      const items = await apiListCampaigns();
      setCampaigns(items);
    } finally {
      setLoading(false);
    }
  }

  async function refreshMailboxes() {
    try {
      const j = await apiListMailboxes();
      const items = Array.isArray(j.items) ? j.items : [];
      const hasConnected = items.some((m) => (m.status || "").toLowerCase() === "connected");
      const skipped = localStorage.getItem(SKIP_KEY) === "1" || Boolean(j?.dismissed);
      setShowConnect(!hasConnected && !skipped);
      setShowBanner(!hasConnected && skipped);
    } catch {
      const skipped = localStorage.getItem(SKIP_KEY) === "1";
      setShowConnect(!skipped);
      setShowBanner(skipped);
    }
  }

  useEffect(() => {
    refreshCampaigns();
  }, []);
  useEffect(() => {
    ensureDefaultTemplate({ setTplLoading, setTplErr, setTemplates, setDefaultTplId });
  }, []);
  useEffect(() => {
    refreshMailboxes();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("psl-lock", showConnect);
    return () => document.body.classList.remove("psl-lock");
  }, [showConnect]);

  async function startGoogle() {
    if (!agree) return setNotice("Please accept the terms first.");
    const j = await apiStartGoogle();
    if (j?.url) window.location.href = j.url;
  }
  async function startOutlook() {
    if (!agree) return setNotice("Please accept the terms first.");
    const j = await apiStartOutlook();
    if (j?.url) window.location.href = j.url;
  }
  async function skipForNow() {
    localStorage.setItem(SKIP_KEY, "1");
    await apiSkipMailbox();
    setShowConnect(false);
    setShowBanner(true);
  }
  function reopenConnect() {
    localStorage.removeItem(SKIP_KEY);
    setShowBanner(false);
    setShowConnect(true);
  }

  const filtered = useMemo(() => {
    let arr = campaigns;
    const qq = qDebounced.trim().toLowerCase();
    if (qq) arr = arr.filter((c) => (c.name || "").toLowerCase().includes(qq) || String(c.id).includes(qq));
    return [...arr].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [campaigns, qDebounced]);

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const start = (page - 1) * pageSize;
  const campRows = filtered.slice(start, start + pageSize);

  const replyPct = (c) => {
    const delivered = n(c.delivered ?? c.emailed);
    const replied = n(c.replied);
    if (!delivered) return "—";
    return `${((replied / delivered) * 100).toFixed(1)}%`;
  };

  const showEmpty = !loading && campaigns.length === 0;

  const [noticeTimer, setNoticeTimer] = useState(null);
  const showNotice = (text, ms = 1800) => {
    setNotice(text);
    if (noticeTimer) clearTimeout(noticeTimer);
    setNoticeTimer(setTimeout(() => setNotice(""), ms));
  };

  const selectedCampaign = useMemo(() => {
    if (openMenuId == null) return null;
    return campaigns.find((c) => c.id === openMenuId) || null;
  }, [openMenuId, campaigns]);

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="psl-subnav">
        <div className="psl-brand">
          <span className="psl-dot" />
          <span>Campaigns</span>
        </div>
      </div>

      <div className="psl-shell campaigns-page">
        <div className="psl-grid" style={{ gridTemplateColumns: "1fr" }}>
          <main className="psl-main" style={{ gridColumn: "1 / -1", minWidth: 0 }}>
            {showBanner && (
              <div className="psl-camp-banner" style={{ marginBottom: 10 }}>
                <div>
                  <b>You have no mailboxes linked.</b>{" "}
                  <span className="psl-muted">Connect your email to start sending via Musicbase.</span>
                </div>
                <button className="psl-btn primary" onClick={reopenConnect}>
                  Link mailbox
                </button>
              </div>
            )}

            <div className="psl-toolbar">
              <div className="psl-actions-row psl-actions-row--search">
                <input
                  className="psl-input psl-input--search"
                  placeholder="Search campaigns…"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="psl-actions-row">
                <button className="psl-btn" onClick={() => { setQ(""); setPage(1); }}>
                  Clear
                </button>
                <button className="psl-btn" onClick={refreshCampaigns}>
                  Refresh
                </button>
                <button className="psl-btn primary" onClick={() => setOpenNew(true)}>
                  New Campaign
                </button>
              </div>
            </div>

            {showEmpty ? (
              <section className="psl-empty">
                <div className="psl-empty-card">
                  <div className="psl-empty-dot" aria-hidden />
                  <h2>Create your first campaign</h2>
                  <p>Build custom campaigns to automate emails, set more meetings, and track replies — all from your own mailbox.</p>
                  <div className="psl-empty-actions">
                    <button className="psl-cta primary" onClick={() => setOpenNew(true)}>
                      Create campaign
                    </button>
                  </div>
                </div>
              </section>
            ) : (
              <>
                <div className="psl-tableblock">
                  <div className="psl-tablewrap">
                    <table className="psl-table">
                      <thead>
                        <tr>
                          <th style={{ width: "22%" }}>Campaign</th>
                          <th style={{ width: "8%" }}>Active</th>
                          <th style={{ width: "8%" }}>Paused</th>
                          <th style={{ width: "9%" }}>Not Sent</th>
                          <th style={{ width: "8%" }}>Bounced</th>
                          <th style={{ width: "10%" }}>Spam Blocked</th>
                          <th style={{ width: "8%" }}>Finished</th>
                          <th style={{ width: "9%" }}>Scheduled</th>
                          <th style={{ width: "8%" }}>Delivered</th>
                          <th style={{ width: "8%" }}>Reply</th>
                          <th style={{ width: "10%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={11} style={{ padding: 16, color: "var(--psl-muted)" }}>Loading…</td>
                          </tr>
                        ) : campRows.length === 0 ? (
                          <tr>
                            <td colSpan={11} style={{ padding: 16, color: "var(--psl-muted)" }}>No campaigns match your search.</td>
                          </tr>
                        ) : (
                          campRows.map((c) => {
                            const counts = {
                              active: n(c.active),
                              paused: n(c.paused),
                              not_sent: Math.max(0, n(c.not_sent ?? n(c.queued) - n(c.emailed))),
                              bounced: n(c.bounced),
                              spam_blocked: n(c.spam_blocked),
                              finished: n(c.finished),
                              scheduled: n(c.scheduled),
                              delivered: n(c.delivered ?? c.emailed),
                              replied: n(c.replied),
                            };
                            const replyDisplay =
                              counts.delivered > 0
                                ? `${counts.replied.toLocaleString()} (${replyPct(c)})`
                                : counts.replied.toLocaleString();

                            const isOpen = openMenuId === c.id;

                            return (
                              <tr key={c.id}>
                                <td>
                                  <b>{c.name || "Campaign"}</b> <span style={{ opacity: 0.7 }}>#{c.id}</span>
                                </td>
                                <td>{counts.active.toLocaleString()}</td>
                                <td>{counts.paused.toLocaleString()}</td>
                                <td>{counts.not_sent.toLocaleString()}</td>
                                <td>{counts.bounced.toLocaleString()}</td>
                                <td>{counts.spam_blocked.toLocaleString()}</td>
                                <td>{counts.finished.toLocaleString()}</td>
                                <td>{counts.scheduled.toLocaleString()}</td>
                                <td>{counts.delivered.toLocaleString()}</td>
                                <td>{replyDisplay}</td>
                                <td className="psl-actioncell">
                                  <button
                                    className="psl-kebab"
                                    aria-haspopup="menu"
                                    aria-expanded={isOpen}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const nextId = openMenuId === c.id ? null : c.id;
                                      if (nextId) {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        const menuWidth = 180;
                                        const left = Math.min(Math.max(8, r.right - menuWidth), window.innerWidth - (menuWidth + 8));
                                        const top = Math.min(r.bottom + 8, window.innerHeight - 8);
                                        setMenuPos({ left, top });
                                      }
                                      setOpenMenuId(nextId);
                                    }}
                                  >
                                    ⋯
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="psl-pager">
                    <div className="psl-pageinfo">
                      Showing {filtered.length === 0 ? 0 : start + 1}–{Math.min(start + pageSize, filtered.length)} of {filtered.length}
                    </div>
                    <div className="psl-pagectrl">
                      <button aria-label="Prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>←</button>
                      <span>Page</span>
                      <select value={page} onChange={(e) => setPage(parseInt(e.target.value, 10))}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <span>of {totalPages}</span>
                      <button aria-label="Next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>→</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      <NewCampaignModal
  open={openNew}
  onClose={() => setOpenNew(false)}
  templates={templates}
  tplLoading={tplLoading}
  tplErr={tplErr}
  defaultTemplateId={defaultTplId}
  onCreate={async (payload) => {
    const id = await apiCreateCampaign(payload);
    setOpenNew(false);
    await refreshCampaigns();
    if (!showCampaignTutorial) {
      navigate(`/campaign/${id}/steps`);
    }
  }}
/>

      {notice && (
        <div className="psl-notice" role="status" aria-live="polite">
          <div>{notice}</div>
        </div>
      )}

      {showConnect && (
        <section className="psl-connect-hero" role="dialog" aria-modal="true">
          <div className="psl-connect-card">
            <button className="psl-hero-skip" onClick={skipForNow}>Skip for now</button>
            <header className="psl-hero-head">
              <div className="psl-hero-dot" aria-hidden />
              <h1>Connect your email</h1>
              <p>Send campaigns from your own inbox for better deliverability and reply rates. Connect a provider to get started.</p>
            </header>

            <div className="psl-provider-grid psl-provider-grid--hero">
              <button className="psl-btn psl-provider-btn psl-provider-btn--lg" onClick={startGoogle} disabled={!agree}>
                <span className="psl-provider-icon psl-provider-icon--green">
                  <img src={GMAIL_LOGO} alt="Gmail" width={22} height={22} loading="lazy" decoding="async" />
                </span>
                <div><b>Google</b><span className="psl-muted" style={{ marginLeft: 6 }}>Gmail / Workspace</span></div>
              </button>
              <button className="psl-btn psl-provider-btn psl-provider-btn--lg" disabled style={{ opacity: 0.5, cursor: "not-allowed" }}>
                <span className="psl-provider-icon psl-provider-icon--blue">
                  <img src={OUTLOOK_LOGO} alt="Outlook" width={22} height={22} loading="lazy" decoding="async" />
                </span>
                <div><b>Outlook</b><span className="psl-muted" style={{ marginLeft: 6 }}>Coming soon</span></div>
              </button>
              <button className="psl-btn psl-provider-btn psl-provider-btn--lg" disabled style={{ opacity: 0.6 }}>
                <span className="psl-provider-icon"><span className="psl-provider-fallback">✉️</span></span>
                <div><b>Other</b><span className="psl-muted" style={{ marginLeft: 6 }}>IMAP / SMTP — soon</span></div>
              </button>
            </div>

            <ul className="psl-hero-bullets">
              <li><span className="check" /> Automated outreach & scheduling</li>
              <li><span className="check" /> Deliverability support</li>
              <li><span className="check" /> Reply tracking & analytics</li>
            </ul>

            <label className="psl-hero-terms">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
              />
              <span>
                I will comply with Musicbase Terms and won’t send spam or harassing content.
                Inbox data may be used to provide deliverability features.
              </span>
            </label>

            {!agree && (
              <p className="psl-hero-terms-note psl-muted">
                Please accept the terms above to enable the Google button.
              </p>
            )}

            <footer className="psl-hero-foot">
              Returning from OAuth? <button className="psl-linkbtn" onClick={refreshMailboxes}>Refresh status</button>
            </footer>
          </div>
        </section>
      )}

      {openMenuId !== null && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="psl-menudrop"
          style={{ left: `${menuPos.left}px`, top: `${menuPos.top}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            role="menuitem"
            onClick={async () => {
              try {
                const newId = await apiCloneCampaign(openMenuId);
                setOpenMenuId(null);
                if (newId) {
                  showNotice("Campaign cloned");
                  await refreshCampaigns();
                } else {
                  showNotice("Could not clone campaign");
                }
              } catch {
                showNotice("Could not clone campaign");
              }
            }}
          >
            Clone
          </button>
          <button
            role="menuitem"
            onClick={() => {
              const id = openMenuId;
              setOpenMenuId(null);
              navigate(`/campaign/${id}/steps`);
            }}
          >
            Edit
          </button>
        </div>,
        document.body
      )}

      {showCampaignTutorial && (
        <CampaignTutorial onClose={() => setShowCampaignTutorial(false)} />
      )}
    </>
  );
}
